// Complete replacement for assets/slideshow.js (ES module).
// Requires the existing theme @theme/* import map and dependencies.
// Opt in on the OUTER carousel tag: continuous-autoplay
// Optional: continuous-autoplay-speed="24" continuous-autoplay-resume-delay="3000"
// Public trigger: document.querySelector('slideshow-component[continuous-autoplay]').startContinuousAutoplay()
// Respects reduced motion and pauses offscreen. Retains next()/previous() for product cards.

import { Component } from '@theme/component';
import {
  center,
  closest,
  clamp,
  mediaQueryLarge,
  prefersReducedMotion,
  preventDefault,
  viewTransition,
  scheduler,
} from '@theme/utilities';
import { Scroller, scrollIntoView } from '@theme/scrolling';
import { SlideshowSelectEvent } from '@theme/events';
// The threshold for determining visibility of slides.
const SLIDE_VISIBLITY_THRESHOLD = 0.7;
/**
 * Shared viewport observer manager for lazy scroll enablement.
 *
 * Limit the number of compositor layers created by slideshows by only enabling scrolling when the slideshow is in the viewport.
 * Resolves known issues with iOS Safari where too many composition layers will crash the page.
 * When a slideshow is NOT in the viewport, it has overflow: hidden (no compositor layer).
 * When a slideshow enters the viewport, the [in-viewport] attribute is added, enabling scrolling.
 */
class SlideshowViewportObserver {
  /** @type {SlideshowViewportObserver | null} */
  static #instance = null;
  /** @type {IntersectionObserver | null} */
  #observer = null;
  /**
   * Gets the singleton instance
   * @returns {SlideshowViewportObserver}
   */
  static getInstance() {
    if (!this.#instance) {
      this.#instance = new SlideshowViewportObserver();
    }
    return this.#instance;
  }
  /**
   * Registers a slideshow to be observed for viewport visibility
   * @param {Slideshow} slideshow - The slideshow to observe
   */
  observe(slideshow) {
    if (!this.#observer) {
      this.#observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            const slideshowElement = /** @type {Slideshow} */ (entry.target);
            if (entry.isIntersecting) {
              slideshowElement.setAttribute('in-viewport', '');
            } else {
              slideshowElement.removeAttribute('in-viewport');
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }
    this.#observer.observe(slideshow);
  }
  /**
   * Unregisters a slideshow from viewport observation
   * @param {Slideshow} slideshow - The slideshow to unobserve
   */
  unobserve(slideshow) {
    this.#observer?.unobserve(slideshow);
    slideshow.removeAttribute('in-viewport');
  }
}
/**
 * Slideshow custom element that allows sliding between content.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} scroller
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLElement} [current]
 * @property {HTMLElement[]} [thumbnails]
 * @property {HTMLElement[]} [dots]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class Slideshow extends Component {
  static get observedAttributes() {
    return ['initial-slide', 'continuous-autoplay', 'in-viewport'];
  }
  /**
   * @param {string} name
   * @param {string} oldValue
   * @param {string} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    if (name === 'continuous-autoplay' || name === 'in-viewport') {
      queueMicrotask(() => {
        if (!this.isConnected || !this.#scroll) return;
        if (this.continuousAutoplay) {
          this.suspend();
          this.#setupContinuousAutoplay();
          if (this.hasAttribute('in-viewport') && !this.#continuousResumeTimeout) {
            this.#startContinuousAutoplay();
          }
        } else if (name === 'continuous-autoplay') {
          this.#stopContinuousAutoplay();
          clearTimeout(this.#continuousResumeTimeout);
          this.#continuousResumeTimeout = undefined;
          this.#continuousController?.abort();
          this.#continuousController = undefined;
          this.resume();
        }
      });
      return;
    }
    // Collection page filtering will Morph slideshow galleries in place, updating
    // the slideshow[initial-slide] and slideshow-slide[hidden] attributes.
    // We need to re-select() the slide after the morph is complete, but not before
    // slideshow-slide elements have their [hidden] attribute updated.
    if (name === 'initial-slide' && oldValue !== newValue) {
      queueMicrotask(() => {
        // Only select if the component is connected and initialized
        if (!this.isConnected || !this.#scroll || !this.refs.slides) return;
        const index = parseInt(newValue, 10) || 0;
        const slide_id = this.refs.slides[index]?.getAttribute('slide-id');
        if (slide_id) {
          this.select({ id: slide_id }, undefined, { animate: false });
        }
      });
    }
  }
  requiredRefs = ['scroller'];
  async connectedCallback() {
    super.connectedCallback();
    // Register with shared viewport observer for lazy scroll enablement.
    // This prevents iOS Safari crashes caused by too many compositor layers.
    SlideshowViewportObserver.getInstance().observe(this);
    // Wait for any in-progress view transitions to finish
    if (viewTransition.current) {
      await viewTransition.current;
      // It's possible that the slideshow was disconnected before the view transition finished
      if (!this.isConnected) return;
    }
    const slideCount = this.slides?.length || 0;
    slideCount <= 1 ? this.#setupSlideshowWithoutControls() : this.#setupSlideshow();
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stopContinuousAutoplay();
    if (this.#continuousResumeTimeout) {
      clearTimeout(this.#continuousResumeTimeout);
      this.#continuousResumeTimeout = undefined;
    }
    this.#continuousController?.abort();
    this.#continuousController = undefined;
    // Unregister from shared viewport observer
    SlideshowViewportObserver.getInstance().unobserve(this);
    if (this.#scroll) {
      const { scroller } = this.refs;
      scroller.removeEventListener('mousedown', this.#handleMouseDown);
      this.#scroll.destroy();
      this.#scroll = undefined;
    }
    const slideCount = this.slides?.length || 0;
    if (slideCount > 1) {
      this.removeEventListener('mouseenter', this.suspend);
      this.removeEventListener('mouseleave', this.resume);
      this.removeEventListener('pointerenter', this.#handlePointerEnter);
      document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    }
    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
    }
    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
      this.#intersectionObserver = null;
    }
  }
  /** Indicates whether the slideshow is nested inside another slideshow. */
  get isNested() {
    return this.parentElement?.closest('slideshow-component') !== null;
  }
  get initialSlide() {
    return this.refs.slides?.[this.initialSlideIndex];
  }
  /**
   * Selects a slide based on the input index.
   * @param {number|string|{id: string}} input - The index or id of the slide to select.
   * @param {Event} [event] - The event that triggered the selection.
   * @param {Object} [options] - The options for the selection.
   * @param {boolean} [options.animate=true] - Whether to animate the selection.
   */
  async select(input, event, options = {}) {
    if (this.#disabled || !this.refs.slides?.length) return;
    if (!this.#scroll) return;
    // Store the actual current slide before any mutations
    const currentSlide = this.slides?.[this.current];
    for (const slide of this.refs.slides) {
      if (slide.hasAttribute('reveal')) {
        slide.removeAttribute('reveal');
        slide.setAttribute('aria-hidden', 'true');
      }
    }
    // Figure out the raw desired index (could be -1 if user is on first slide and clicks prev)
    let requestedIndex = (() => {
      if (typeof input === 'number') return input;
      if (typeof input === 'string') return parseInt(input, 10);
      if ('id' in input) {
        const requestedSlide = this.refs.slides.find((slide) => slide.getAttribute('slide-id') == input.id);
        if (!requestedSlide || !this.slides) return;
        // Force the slide to be revealed if it is hidden
        if (requestedSlide.hasAttribute('hidden')) {
          requestedSlide.setAttribute('reveal', '');
          requestedSlide.setAttribute('aria-hidden', 'false');
        }
        return this.slides.indexOf(requestedSlide);
      }
    })();
    const { current } = this;
    const { slides } = this;
    // Guard checks: no slides, invalid index, or selecting the same slide
    if (!slides?.length || requestedIndex === undefined || isNaN(requestedIndex)) return;
    const requestedSlideElement = slides?.[requestedIndex];
    if (currentSlide === requestedSlideElement) return;
    if (!this.infinite) requestedIndex = clamp(requestedIndex, 0, slides.length - 1);
    event?.preventDefault();
    const { animate = true } = options;
    const lastIndex = slides.length - 1;
    // Decide the actual target index (clamp for infinite loop)
    let index = requestedIndex;
    if (requestedIndex < 0) index = lastIndex;
    else if (requestedIndex > lastIndex) index = 0;
    const isAdjacentSlide = Math.abs(index - current) <= 1 && requestedIndex >= 0 && requestedIndex <= lastIndex;
    const { visibleSlides } = this;
    const instant = prefersReducedMotion() || !animate;
    // If jump is more than 1 or we looped, do the placeholder + reorder trick
    if (!instant && !isAdjacentSlide && visibleSlides.length === 1) {
      this.#disabled = true;
      await this.#scroll.finished; // ensure we're not mid-scroll
      const targetSlide = slides[index];
      if (!targetSlide || !currentSlide) return;
      // Create a placeholder in the original DOM position of targetSlide
      const placeholder = document.createElement('slideshow-slide');
      targetSlide.before(placeholder);
      // Decide whether targetSlide goes before or after currentSlide
      // so that we scroll a short distance in the correct direction
      if (requestedIndex < current) {
        currentSlide.before(targetSlide);
      } else {
        currentSlide.after(targetSlide);
      }
      if (current === 0) this.#scroll.to(currentSlide, { instant: true });
      // Once that scroll finishes, restore the DOM
      queueMicrotask(async () => {
        await this.#scroll.finished;
        this.#disabled = false;
        // Restore the slide back to its original position. This triggers a scroll event.
        placeholder.replaceWith(targetSlide);
        // Instantly scroll to the target slide as its position will have changed
        this.#scroll.to(targetSlide, { instant: true });
        // Force Safari to recalculate the timeline state on timeline refresh (after loop)
        requestAnimationFrame(() => {
          this.setAttribute('refreshing-timeline', '');
          requestAnimationFrame(() => {
            this.removeAttribute('refreshing-timeline');
          });
        });
      });
    }
    const slide = slides[index];
    if (!slide) return;
    const previousIndex = this.current;
    slide.setAttribute('aria-hidden', 'false');
    if (this.#scroll) {
      this.#scroll.to(slide, { instant });
    }
    this.current = this.slides?.indexOf(slide) || 0;
    this.#centerSelectedThumbnail(index, instant ? 'instant' : 'smooth');
    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: event != null,
        trigger: 'select',
        slide,
        id: slide.getAttribute('slide-id'),
      })
    );
  }
  /**
   * Advances to the next slide.
   * @param {Event} [event] - The event that triggered the next slide.
   * @param {Object} [options] - The options for the next slide.
   * @param {boolean} [options.animate=true] - Whether to animate the next slide.
   */
  next(event, options) {
    event?.preventDefault();
    this.select(this.nextIndex, event, options);
  }
  /**
   * Goes back to the previous slide.
   * @param {Event} [event] - The event that triggered the previous slide.
   * @param {Object} [options] - The options for the previous slide.
   * @param {boolean} [options.animate=true] - Whether to animate the previous slide.
   */
  previous(event, options) {
    event?.preventDefault();
    this.select(this.previousIndex, event, options);
  }
  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (this.#interval) return;
    this.paused = false;
    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;
      this.next();
    }, interval);
  }
  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
    this.#stopContinuousAutoplay();
  }
  get paused() {
    return this.hasAttribute('paused');
  }
  set paused(value) {
    if (value) {
      this.setAttribute('paused', '');
    } else {
      this.removeAttribute('paused');
    }
  }
  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }
  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    // Traditional slide-by-slide autoplay must not run at the same time
    // as continuous autoplay, otherwise this.next() will create periodic jumps.
    if (this.continuousAutoplay) return;
    if (!this.autoplay || this.paused) return;
    this.pause();
    this.play();
  }
  get autoplay() {
    return Boolean(this.autoplayInterval);
  }
  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);
    if (Number.isNaN(value)) return undefined;
    return value * 1000;
  }
  /** Whether continuous scrolling autoplay is enabled. */
  get continuousAutoplay() {
    return this.hasAttribute('continuous-autoplay');
  }
  /** Continuous autoplay speed in pixels per second. */
  get continuousAutoplaySpeed() {
    const value = parseFloat(this.getAttribute('continuous-autoplay-speed') || '');
    return Number.isFinite(value) && value > 0 ? value : 18;
  }
  /** Delay before continuous autoplay resumes after interaction, in milliseconds. */
  get continuousAutoplayResumeDelay() {
    const value = parseInt(this.getAttribute('continuous-autoplay-resume-delay') || '', 10);
    return Number.isFinite(value) && value >= 0 ? value : 3000;
  }
  /**
   * The current slide index.
   * @type {number}
   */
  #current = 0;
  get current() {
    return this.#current;
  }
  /**
   * Sets the current slide index and update the DOM
   * @type {number}
   */
  set current(value) {
    const { current, thumbnails, dots, slides, previous, next } = this.refs;
    this.#current = value;
    if (current) current.textContent = `${value + 1}`;
    for (const controls of [thumbnails, dots]) {
      controls?.forEach((el, i) => el.setAttribute('aria-selected', `${i === value}`));
    }
    if (previous) previous.disabled = Boolean(!this.infinite && value === 0);
    if (next) next.disabled = Boolean(!this.infinite && slides && this.nextIndex >= slides.length);
  }
  get infinite() {
    return this.getAttribute('infinite') != null;
  }
  get visibleSlides() {
    return this.#visibleSlides;
  }
  get previousIndex() {
    const { current, visibleSlides } = this;
    const modifier = visibleSlides.length > 1 ? visibleSlides.length : 1;
    return current - modifier;
  }
  get nextIndex() {
    const { current, visibleSlides } = this;
    const modifier = visibleSlides.length > 1 ? visibleSlides.length : 1;
    return current + modifier;
  }
  get atStart() {
    const { current, slides } = this;
    return slides?.length ? current === 0 : false;
  }
  get atEnd() {
    const { current, slides } = this;
    return slides?.length ? current === slides.length - 1 : false;
  }
  /**
   * Sets the disabled attribute.
   * @param {boolean} value - The value to set the disabled attribute to.
   */
  set disabled(value) {
    this.setAttribute('disabled', String(value));
  }
  /**
   * Whether the slideshow is disabled.
   * @type {boolean}
   */
  get disabled() {
    return (
      this.getAttribute('disabled') === 'true' || (this.hasAttribute('mobile-disabled') && !mediaQueryLarge.matches)
    );
  }
  /**
   * Indicates whether the slideshow is temporarily disabled (e.g., during infinite loop transition).
   * @type {boolean}
   */
  #disabled = false;
  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;
  /** @type {number|undefined} */
  #continuousFrame = undefined;
  /** @type {number|undefined} */
  #continuousResumeTimeout = undefined;
  /** @type {number|undefined} */
  #continuousLastTime = undefined;
  #continuousPlaying = false;
  #continuousStyles;

  // Public console/API controls for the continuous engine.
  startContinuousAutoplay() {
    this.setAttribute('continuous-autoplay', '');
    this.paused = false;
    this.suspend();
    if (!this.#scroll) return;
    this.#setupContinuousAutoplay();
    this.#startContinuousAutoplay();
  }

  stopContinuousAutoplay() {
    this.paused = true;
    clearTimeout(this.#continuousResumeTimeout);
    this.#continuousResumeTimeout = undefined;
    this.#stopContinuousAutoplay();
  }
  #continuousPointerActive = false;
  /** @type {AbortController|undefined} */
  #continuousController = undefined;
  /**
   * The Scroller instance that manages scrolling.
   * @type {Scroller}
   */
  #scroll;
  /**
   * The ResizeObserver instance for monitoring scroller size changes
   * @type {ResizeObserver}
   */
  #resizeObserver;
  /**
   * IntersectionObserver for efficient visibility tracking of slides
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;
  /**
   * Cached visible slides result from IntersectionObserver
   * @type {HTMLElement[]}
   */
  #visibleSlides = [];
  /**
   * Setup the slideshow without controls for zero or one slides
   */
  #setupSlideshowWithoutControls() {
    this.current = 0;
    if (this.hasAttribute('auto-hide-controls')) {
      const { slideshowControls } = this.refs;
      if (slideshowControls instanceof HTMLElement) {
        slideshowControls.hidden = true;
      }
    }
    if (this.refs.slides?.[0]) {
      this.refs.slides[0].setAttribute('aria-hidden', 'false');
    }
  }
  /**
   * Setup the slideshow with controls for when there are multiple slides
   */
  #setupSlideshow() {
    // Setup IntersectionObserver first for efficient visibility tracking
    this.#setupIntersectionObserver();
    // Setup the scroll instance
    const { scroller } = this.refs;
    this.#scroll = new Scroller(scroller, {
      onScroll: this.#handleScroll,
      onScrollStart: this.#onTransitionInit,
      onScrollEnd: this.#onTransitionEnd,
    });
    scroller.addEventListener('mousedown', this.#handleMouseDown);
    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    this.addEventListener('pointerenter', this.#handlePointerEnter);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
    this.#updateControlsVisibility();
    this.disabled = this.isNested || this.disabled;
    this.current = this.initialSlideIndex;
    // Use only one autoplay engine at a time. Traditional autoplay advances
    // whole slides with this.next(); continuous autoplay moves every frame.
    if (this.continuousAutoplay) {
      this.suspend();
      this.#setupContinuousAutoplay();
    } else {
      this.resume();
    }
    // Batch reads and writes to the DOM
    scheduler.schedule(() => {
      let visibleSlidesAmount = 0;
      const initialSlideId = this.initialSlide?.getAttribute('slide-id');
      // Wait for next frame to ensure layout is fully calculated before setting initial scroll position
      // This prevents race conditions on Safari mobile when section_width is 'full-width'
      requestAnimationFrame(() => {
        if (this.initialSlideIndex !== 0 && initialSlideId) {
          this.select({ id: initialSlideId }, undefined, { animate: false });
          visibleSlidesAmount = 1;
        } else {
          visibleSlidesAmount = this.#updateVisibleSlides();
          if (visibleSlidesAmount === 0) {
            this.select(0, undefined, { animate: false });
            visibleSlidesAmount = 1;
          }
        }
      });
      this.#resizeObserver = new ResizeObserver(async () => {
        if (viewTransition.current) await viewTransition.current;
        if (visibleSlidesAmount > 1) {
          this.#updateVisibleSlides();
        }
        if (this.hasAttribute('auto-hide-controls')) {
          this.#updateControlsVisibility();
        }
        if (this.continuousAutoplay && !this.#continuousPlaying && !this.#continuousPointerActive) {
          this.#startContinuousAutoplay();
        }
      });
      this.#resizeObserver.observe(this.refs.slideshowContainer || this.refs.scroller);
    });
  }
  /**
   * Sets up continuous autoplay for carousels that opt in with
   * the `continuous-autoplay` attribute.
   */
  #setupContinuousAutoplay() {
    if (!this.continuousAutoplay || this.#continuousController) return;
    const controller = new AbortController();
    const { signal } = controller;
    this.#continuousController = controller;
    this.addEventListener('pointerdown', this.#handleContinuousPointerDown, { signal, passive: true });
    document.addEventListener('pointerup', this.#handleContinuousPointerUp, { signal, passive: true });
    document.addEventListener('pointercancel', this.#handleContinuousPointerUp, { signal, passive: true });
    this.addEventListener('wheel', this.#handleContinuousActivity, { signal, passive: true });
    this.addEventListener('keydown', this.#handleContinuousActivity, { signal });
    document.addEventListener('visibilitychange', this.#handleContinuousVisibility, { signal });
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    motion.addEventListener('change', () => {
      if (motion.matches) this.#stopContinuousAutoplay();
      else this.#scheduleContinuousAutoplay();
    }, { signal });
    // Start once layout provides scrollable content.
    requestAnimationFrame(() => this.#startContinuousAutoplay());
  }
  /** Starts the slow, continuous left-to-right scroll. */
  #startContinuousAutoplay = () => {
    if (!this.continuousAutoplay || !this.#scroll || !this.isConnected || this.paused) return;
    if (document.hidden || this.#dragging || this.#continuousPointerActive) return;
    if (prefersReducedMotion()) return;
    if (this.#continuousPlaying) return;
    if (this.#continuousResumeTimeout) {
      clearTimeout(this.#continuousResumeTimeout);
      this.#continuousResumeTimeout = undefined;
    }
    if (this.#continuousFrame) {
      cancelAnimationFrame(this.#continuousFrame);
      this.#continuousFrame = undefined;
    }
    this.#continuousPlaying = true;
    this.#continuousLastTime = undefined;
    // Continuous movement and CSS scroll snapping fight each other,
    // so snapping stays off only while autoplay itself is moving.
    const scroller = this.refs.scroller;
    if (!this.#continuousStyles) {
      this.#continuousStyles = ['scroll-snap-type', 'scroll-behavior'].map(name => ({
        name, value: scroller.style.getPropertyValue(name),
        priority: scroller.style.getPropertyPriority(name)
      }));
    }
    this.#scroll.snap = false;
    scroller.style.setProperty('scroll-snap-type', 'none', 'important');
    scroller.style.setProperty('scroll-behavior', 'auto', 'important');
    // Keep fractional progress between frames; resync after each pause/restart.
    let continuousPosition = this.refs.scroller.scrollLeft;
    const animate = (timestamp) => {
      if (!this.#continuousPlaying || !this.isConnected) return;
      if (!this.continuousAutoplay || this.paused || prefersReducedMotion()) {
        this.#stopContinuousAutoplay();
        return;
      }
      if (document.hidden || this.#dragging || this.#continuousPointerActive) {
        this.#stopContinuousAutoplay();
        return;
      }
      // The theme already tracks viewport visibility. Avoid doing scroll work
      // when this slideshow is well outside the viewport.
      if (!this.hasAttribute('in-viewport')) {
        this.#continuousLastTime = timestamp;
        this.#continuousFrame = requestAnimationFrame(animate);
        return;
      }
      if (this.#continuousLastTime === undefined) {
        this.#continuousLastTime = timestamp;
        this.#continuousFrame = requestAnimationFrame(animate);
        return;
      }
      const deltaSeconds = Math.min((timestamp - this.#continuousLastTime) / 1000, 0.05);
      this.#continuousLastTime = timestamp;
      // Accumulate sub-pixel movement independently of browser rounding.
      const distance = this.continuousAutoplaySpeed * deltaSeconds;
      const { scroller } = this.refs;
      const maxScroll = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
      continuousPosition = Math.min(continuousPosition + distance, maxScroll);
      scroller.scrollLeft = continuousPosition;
      // Wait for images/layout if there is not yet enough content to scroll.
      // At the end, return to the first item and keep the autoplay alive.
      if (maxScroll > 0 && continuousPosition >= maxScroll - 0.1) {
        continuousPosition = 0;
        scroller.scrollLeft = 0;
        this.#continuousLastTime = undefined;
      }
      this.#continuousFrame = requestAnimationFrame(animate);
    };
    this.#continuousFrame = requestAnimationFrame(animate);
  };
  /** Stops continuous movement and restores normal snapping. */
  #stopContinuousAutoplay = () => {
    this.#continuousPlaying = false;
    if (this.#continuousFrame) {
      cancelAnimationFrame(this.#continuousFrame);
      this.#continuousFrame = undefined;
    }
    this.#continuousLastTime = undefined;
    if (this.#scroll) {
      this.#scroll.snap = true;
    }
    if (this.#continuousStyles) {
      const scroller = this.refs.scroller;
      for (const { name, value, priority } of this.#continuousStyles) {
        if (value) scroller.style.setProperty(name, value, priority);
        else scroller.style.removeProperty(name);
      }
      this.#continuousStyles = undefined;
    }
  };
  /** Schedules autoplay to resume after the configured inactivity delay. */
  #scheduleContinuousAutoplay = () => {
    if (!this.continuousAutoplay) return;
    if (this.#continuousResumeTimeout) {
      clearTimeout(this.#continuousResumeTimeout);
    }
    this.#continuousResumeTimeout = window.setTimeout(() => {
      this.#continuousResumeTimeout = undefined;
      if (this.#continuousPointerActive || this.#dragging || document.hidden) return;
      this.#startContinuousAutoplay();
    }, this.continuousAutoplayResumeDelay);
  };
  /** Wheel or keyboard activity pauses autoplay and resets the inactivity timer. */
  #handleContinuousActivity = () => {
    if (!this.continuousAutoplay) return;
    this.#stopContinuousAutoplay();
    this.#scheduleContinuousAutoplay();
  };
  /** Pointer/touch interaction pauses immediately. */
  #handleContinuousPointerDown = () => {
    if (!this.continuousAutoplay) return;
    this.#continuousPointerActive = true;
    if (this.#continuousResumeTimeout) {
      clearTimeout(this.#continuousResumeTimeout);
      this.#continuousResumeTimeout = undefined;
    }
    this.#stopContinuousAutoplay();
  };
  /** Start the 3-second inactivity countdown after pointer/touch release. */
  #handleContinuousPointerUp = () => {
    if (!this.continuousAutoplay || !this.#continuousPointerActive) return;
    this.#continuousPointerActive = false;
    this.#scheduleContinuousAutoplay();
  };
  /** Pause while the browser tab is hidden and resume after inactivity when visible again. */
  #handleContinuousVisibility = () => {
    if (!this.continuousAutoplay) return;
    if (document.hidden) {
      this.#stopContinuousAutoplay();
      if (this.#continuousResumeTimeout) {
        clearTimeout(this.#continuousResumeTimeout);
        this.#continuousResumeTimeout = undefined;
      }
      return;
    }
    this.#scheduleContinuousAutoplay();
  };
  /**
   * Callback invoked on user initiated scroll to sync the current slide index
   * and emit a slide change event if the index has changed.
   */
  #handleScroll = () => {
    const previousIndex = this.#current;
    const index = this.#sync();
    if (index === previousIndex) return;
    const slide = this.slides?.[index];
    if (!slide) return;
    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: !this.#continuousPlaying,
        trigger: 'scroll',
        slide,
        id: slide.getAttribute('slide-id'),
      })
    );
  };
  #onTransitionInit = () => {
    this.setAttribute('transitioning', '');
  };
  #onTransitionEnd = () => {
    this.#updateVisibleSlides();
    this.removeAttribute('transitioning');
  };
  /**
   * Synchronizes the scroll position and updates the current slide index.
   * @returns {number} The index of the current slide.
   */
  #sync = () => {
    const { slides } = this;
    if (!slides) return (this.current = 0);
    if (!this.#scroll) return (this.current = 0);
    const visibleSlides = this.visibleSlides;
    if (!visibleSlides.length) return this.current;
    const { axis } = this.#scroll;
    const { scroller } = this.refs;
    const centers = visibleSlides.map((slide) => center(slide, axis));
    const referencePoint = visibleSlides.length > 1 ? scroller.getBoundingClientRect()[axis] : center(scroller, axis);
    const closestCenter = closest(centers, referencePoint);
    const closestVisibleSlide = visibleSlides[centers.indexOf(closestCenter)];
    if (!closestVisibleSlide) return (this.current = 0);
    const index = slides.indexOf(closestVisibleSlide);
    return (this.current = index);
  };
  #dragging = false;
  /**
   * Handles the 'mousedown' event to start dragging slides.
   * @param {MouseEvent} event - The mousedown event.
   */
  #handleMouseDown = (event) => {
    const { slides } = this;
    if (!slides || slides.length <= 1) return;
    if (!(event.target instanceof Element)) return;
    if (this.disabled || this.#dragging) return;
    // Check if the event target is within a 3D model interactive element
    // This prevents the slideshow from capturing drag events when interacting with 3D models
    if (event.target.closest('model-viewer')) {
      return;
    }
    event.preventDefault();
    // Store initial position but don't start handling yet
    const { axis } = this.#scroll;
    const startPosition = event[axis];
    const controller = new AbortController();
    const { signal } = controller;
    const startTime = performance.now();
    let previous = startPosition;
    let velocity = 0;
    let moved = false;
    let distanceTravelled = 0;
    this.#dragging = true;
    /**
     * Handles the 'pointermove' event to update the scroll position.
     * @param {PointerEvent} event - The pointermove event.
     */
    const onPointerMove = (event) => {
      const current = event[axis];
      const initialDelta = startPosition - current;
      if (!initialDelta) return;
      if (!moved) {
        moved = true;
        this.setPointerCapture(event.pointerId);
        // Prevent clicks once the user starts dragging
        document.addEventListener('click', preventDefault, { once: true, signal });
        const movingRight = initialDelta < 0;
        const movingLeft = initialDelta > 0;
        // Check if the current slideshow should handle this drag
        const closestSlideshow = this.parentElement?.closest('slideshow-component');
        const isNested = closestSlideshow instanceof Slideshow && closestSlideshow !== this;
        const cannotMoveInDirection = (movingRight && this.atStart) || (movingLeft && this.atEnd);
        // Abort and let the parent slideshow handle the drag if we're moving in a direction where nested slideshow can't move
        if (isNested && cannotMoveInDirection) {
          controller.abort();
          return;
        }
        if (this.continuousAutoplay) this.#stopContinuousAutoplay();
        else this.pause();
        this.setAttribute('dragging', '');
      }
      // Stop the event from bubbling up to parent slideshow components
      event.stopImmediatePropagation();
      const delta = previous - current;
      const timeDelta = performance.now() - startTime;
      velocity = Math.round((delta / timeDelta) * 1000);
      previous = current;
      distanceTravelled += Math.abs(delta);
      this.#scroll.by(delta, { instant: true });
    };
    /**
     * Handles the 'pointerup' event to stop dragging slides.
     * @param {PointerEvent} event - The pointerup event.
     */
    const onPointerUp = async (event) => {
      controller.abort();
      const { current, slides } = this;
      const { scroller } = this.refs;
      this.#dragging = false;
      if (!slides?.length || !scroller) return;
      const direction = Math.sign(velocity);
      const next = this.#sync();
      const modifier = current !== next || Math.abs(velocity) < 10 || distanceTravelled < 10 ? 0 : direction;
      const newIndex = clamp(next + modifier, 0, slides.length - 1);
      const newSlide = slides[newIndex];
      const currentIndex = this.current;
      if (!newSlide) throw new Error(`Slide not found at index ${newIndex}`);
      this.#scroll.to(newSlide);
      this.removeAttribute('dragging');
      this.releasePointerCapture(event.pointerId);
      this.#centerSelectedThumbnail(newIndex);
      this.dispatchEvent(
        new SlideshowSelectEvent({
          index: newIndex,
          previousIndex: currentIndex,
          userInitiated: true,
          trigger: 'drag',
          slide: newSlide,
          id: newSlide.getAttribute('slide-id'),
        })
      );
      this.current = newIndex;
      await this.#scroll.finished;
      // It's possible that the user started dragging again before the scroll finished
      if (this.#dragging) return;
      this.#scroll.snap = true;
      if (this.continuousAutoplay) {
        this.#scheduleContinuousAutoplay();
      } else {
        this.resume();
      }
    };
    this.#scroll.snap = false;
    document.addEventListener('pointermove', onPointerMove, { signal });
    document.addEventListener('pointerup', onPointerUp, { signal });
    /**
     * pointerDown calls onPointerUp to fix an issue where the first tap-and-drag
     * on the zoom dialog is captured by the pointerMove/pointerUp listeners,
     * sometimes causing the slideshow to change slides unexpectedly
     */
    document.addEventListener('pointerdown', onPointerUp, { signal });
    document.addEventListener('pointercancel', onPointerUp, { signal });
    document.addEventListener('pointercapturelost', onPointerUp, { signal });
  };
  #handlePointerEnter = () => {
    this.setAttribute('actioned', '');
  };
  get slides() {
    return this.refs.slides?.filter((slide) => !slide.hasAttribute('hidden') || slide.hasAttribute('reveal'));
  }
  /**
   * The initial slide index.
   * @type {number}
   */
  get initialSlideIndex() {
    const initialSlide = this.getAttribute('initial-slide');
    if (initialSlide == null) return 0;
    return parseInt(initialSlide, 10);
  }
  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.suspend() : this.resume());
  #updateControlsVisibility() {
    if (!this.hasAttribute('auto-hide-controls')) return;
    const { scroller, slideshowControls } = this.refs;
    if (!(slideshowControls instanceof HTMLElement)) return;
    slideshowControls.hidden = scroller.scrollWidth <= scroller.offsetWidth;
  }
  /**
   * Setup IntersectionObserver for efficient visibility tracking of slides
   */
  #setupIntersectionObserver() {
    const { slides, scroller } = this.refs;
    if (!slides?.length) return;
    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
    }
    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        const allEntries = [
          ...entries,
          ...(this.#intersectionObserver ? this.#intersectionObserver.takeRecords() : []),
        ];
        for (const entry of allEntries) {
          const slide = /** @type {HTMLElement} */ (entry.target);
          const isCurrentlyVisible = this.#visibleSlides.includes(slide);
          const shouldBeVisible = entry.intersectionRatio >= SLIDE_VISIBLITY_THRESHOLD;
          if (shouldBeVisible && !isCurrentlyVisible) {
            this.#visibleSlides.push(slide);
          } else if (!shouldBeVisible && isCurrentlyVisible) {
            const index = this.#visibleSlides.indexOf(slide);
            if (index > -1) {
              this.#visibleSlides.splice(index, 1);
            }
          }
        }
        this.#visibleSlides.sort((a, b) => slides.indexOf(a) - slides.indexOf(b));
        this.#updateVisibleSlides();
      },
      {
        root: scroller,
        threshold: SLIDE_VISIBLITY_THRESHOLD,
        // Add small margin to account for sub-pixel rendering
        rootMargin: '1px',
      }
    );
    // Observe all slides - observer will fire initial callback asynchronously
    slides.forEach((slide) => {
      this.#intersectionObserver?.observe(slide);
    });
  }
  /**
   * Centers the selected thumbnail in the thumbnails container
   * @param {number} index - The index of the selected thumbnail
   * @param {ScrollBehavior} [behavior] - The scroll behavior.
   */
  #centerSelectedThumbnail(index, behavior = 'smooth') {
    const selectedThumbnail = this.refs.thumbnails?.[index];
    if (!selectedThumbnail) return;
    const { thumbnailsContainer } = this.refs;
    if (!thumbnailsContainer || !(thumbnailsContainer instanceof HTMLElement)) return;
    const { slideshowControls } = this.refs;
    if (!slideshowControls || !(slideshowControls instanceof HTMLElement)) return;
    scrollIntoView(selectedThumbnail, {
      ancestor: thumbnailsContainer,
      behavior,
      block: 'center',
      inline: 'center',
    });
  }
  #updateVisibleSlides() {
    const { slides } = this;
    if (!slides || !slides.length) return 0;
    const visibleSlides = this.visibleSlides;
    // Batch writes to the DOM
    scheduler.schedule(() => {
      // Update aria-hidden based on visibility
      slides.forEach((slide) => {
        const isVisible = visibleSlides.includes(slide);
        slide.setAttribute('aria-hidden', `${!isVisible}`);
      });
    });
    return visibleSlides.length;
  }
}
if (!customElements.get('slideshow-component')) {
  customElements.define('slideshow-component', Slideshow);
}

